import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, LogIn, LogOut, Thermometer, MapPin, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

export default function EmployeePortal() {
  const [token, setToken] = useState(localStorage.getItem('portal_token'));
  const [user, setUser] = useState(null);
  const [email, setEmail] = useState(localStorage.getItem('portal_email') || '');
  const [password, setPassword] = useState('');
  const [activeEntry, setActiveEntry] = useState(null);
  const [entries, setEntries] = useState([]);
  const [sickLeaves, setSickLeaves] = useState([]);
  const [location, setLocation] = useState(null);
  const [locError, setLocError] = useState(null);
  const [commentIn, setCommentIn] = useState('');
  const [commentOut, setCommentOut] = useState('');
  const [sickOpen, setSickOpen] = useState(false);
  const [sickDate, setSickDate] = useState('');
  const [loading, setLoading] = useState(false);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Request location
  useEffect(() => {
    if (!token) return;
    if (!navigator.geolocation) {
      setLocError('Geolocation is not supported by your browser');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setLocError('Location access is required to use this portal'),
    );
  }, [token]);

  // Fetch user info from token
  useEffect(() => {
    if (!token) return;
    const stored = localStorage.getItem('portal_user');
    if (stored) setUser(JSON.parse(stored));
  }, [token]);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const [activeRes, entriesRes] = await Promise.all([
        axios.get(`${API}/time/active`, { headers: headers() }),
        axios.get(`${API}/portal/my-entries`, { headers: headers() }),
      ]);
      setActiveEntry(activeRes.data.active ? activeRes.data.entry : null);
      setEntries(entriesRes.data.entries || []);
      setSickLeaves(entriesRes.data.sick_leaves || []);
    } catch {
      // Token might be invalid
      handleLogout();
    }
  }, [token, headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      const t = res.data.token;
      localStorage.setItem('portal_token', t);
      localStorage.setItem('portal_user', JSON.stringify(res.data.user));
      localStorage.setItem('portal_email', email);
      setToken(t);
      setUser(res.data.user);
      toast.success('Logged in!');
    } catch {
      toast.error('Wrong email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    // Keep portal_email so they don't have to type it again
    setToken(null);
    setUser(null);
  };

  const [busy, setBusy] = useState(false);

  const handleClockIn = async () => {
    if (!location) {
      toast.error('Location is required');
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/time/clock-in`, {
        location_lat: location.lat,
        location_lng: location.lng,
        comment: commentIn || null,
      }, { headers: headers() });
      toast.success('Clocked in!');
      setCommentIn('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock in');
    } finally {
      setBusy(false);
    }
  };

  const handleClockOut = async () => {
    if (!activeEntry || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/time/clock-out`, {
        entry_id: activeEntry.id,
        comment: commentOut || null,
      }, { headers: headers() });
      toast.success('Clocked out!');
      setCommentOut('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to clock out');
    } finally {
      setBusy(false);
    }
  };

  const handleSickDay = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/portal/sick-day`, { date: sickDate }, { headers: headers() });
      toast.success('Sick day registered!');
      setSickOpen(false);
      setSickDate('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register');
    }
  };

  // Login screen
  if (!token || !user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="portal-login">
        <Card className="w-full max-w-sm">
          <CardContent className="pt-8 pb-6 space-y-6">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-indigo-100 mb-2">
                <Clock className="h-7 w-7 text-indigo-600" />
              </div>
              <h1 className="text-xl font-bold">Employee Portal</h1>
              <p className="text-sm text-gray-500">Sign in to manage your time</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="portal-email"
                />
              </div>
              <div className="space-y-2">
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus={!!email}
                  autoComplete="current-password"
                  data-testid="portal-password"
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="portal-login-btn">
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Location required
  if (locError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-sm">
          <CardContent className="py-12 text-center space-y-4">
            <MapPin className="h-12 w-12 text-red-500 mx-auto" />
            <p className="text-lg font-semibold text-red-600">{locError}</p>
            <p className="text-sm text-gray-500">Please allow location access and reload the page.</p>
            <Button onClick={() => window.location.reload()} variant="outline">Reload</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!location) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <p className="text-gray-500">Getting your location...</p>
      </div>
    );
  }

  const recentEntries = entries.slice(0, 10);
  const recentSick = sickLeaves.slice(0, 5);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-page">
      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">{user.full_name}</h1>
            <p className="text-xs text-gray-500 flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Location verified
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={handleLogout} data-testid="portal-logout">
            Sign out
          </Button>
        </div>

        {/* Clock In / Out */}
        {!activeEntry ? (
          <Card className="border-green-200 bg-green-50" data-testid="portal-clock-in-card">
            <CardContent className="py-6 space-y-4">
              <div className="text-center">
                <LogIn className="h-10 w-10 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-lg">Ready to clock in</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Comment (optional)</Label>
                <Input
                  value={commentIn}
                  onChange={(e) => setCommentIn(e.target.value)}
                  placeholder="e.g. Working from office"
                  data-testid="portal-comment-in"
                />
              </div>
              <Button onClick={handleClockIn} className="w-full bg-green-600 hover:bg-green-700" disabled={busy} data-testid="portal-clock-in-btn">
                <LogIn className="h-4 w-4 mr-2" />
                {busy ? 'Clocking in...' : 'Clock In'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-red-200 bg-red-50" data-testid="portal-clock-out-card">
            <CardContent className="py-6 space-y-4">
              <div className="text-center">
                <LogOut className="h-10 w-10 text-red-600 mx-auto mb-2" />
                <p className="font-semibold text-lg">Clocked in since {fmtTime(activeEntry.clock_in)}</p>
                {activeEntry.comment_in && (
                  <p className="text-sm text-gray-500 mt-1">{activeEntry.comment_in}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm">Comment (optional)</Label>
                <Input
                  value={commentOut}
                  onChange={(e) => setCommentOut(e.target.value)}
                  placeholder="e.g. Finished for the day"
                  data-testid="portal-comment-out"
                />
              </div>
              <Button onClick={handleClockOut} className="w-full bg-red-600 hover:bg-red-700" disabled={busy} data-testid="portal-clock-out-btn">
                <LogOut className="h-4 w-4 mr-2" />
                {busy ? 'Clocking out...' : 'Clock Out'}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Report sick */}
        <Button
          variant="outline"
          className="w-full text-orange-600 border-orange-300 hover:bg-orange-50"
          onClick={() => setSickOpen(true)}
          data-testid="portal-sick-btn"
        >
          <Thermometer className="h-4 w-4 mr-2" />
          Report sick day
        </Button>

        {/* Recent sick days */}
        {recentSick.length > 0 && (
          <Card className="bg-orange-50 border-orange-200">
            <CardContent className="py-4">
              <p className="font-semibold text-sm mb-2 flex items-center gap-1">
                <Thermometer className="h-4 w-4 text-orange-600" />
                Recent sick days
              </p>
              <div className="divide-y divide-orange-200">
                {recentSick.map((s) => (
                  <div key={s.id} className="py-1.5 text-sm flex justify-between">
                    <span>{fmtDate(s.start_date + 'T00:00:00')}</span>
                    <span className="text-orange-700 font-medium">7.5 hrs</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent entries */}
        <div className="space-y-2">
          <p className="font-semibold text-sm">Recent entries</p>
          {recentEntries.length === 0 ? (
            <Card><CardContent className="py-6 text-center text-gray-400 text-sm">No entries yet</CardContent></Card>
          ) : (
            recentEntries.map((entry) => (
              <Card key={entry.id} className="border-gray-200" data-testid={`portal-entry-${entry.id}`}>
                <CardContent className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-sm">{fmtDate(entry.clock_in)}</p>
                      <p className="text-xs text-gray-500">
                        In: {fmtTime(entry.clock_in)}
                        {entry.clock_out ? ` · Out: ${fmtTime(entry.clock_out)}` : ' · Working...'}
                        {entry.total_hours ? ` · ${entry.total_hours} hrs` : ''}
                      </p>
                      {entry.comment_in && <p className="text-xs text-blue-600 mt-0.5">In: {entry.comment_in}</p>}
                      {entry.comment_out && <p className="text-xs text-blue-600">Out: {entry.comment_out}</p>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      entry.status === 'approved' ? 'bg-green-100 text-green-700' :
                      entry.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      'bg-yellow-100 text-yellow-700'
                    }`}>
                      {entry.status === 'approved' ? 'Approved' : entry.status === 'rejected' ? 'Rejected' : 'Pending'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Sick Day Dialog */}
        <Dialog open={sickOpen} onOpenChange={setSickOpen}>
          <DialogContent aria-describedby="portal-sick-desc">
            <DialogHeader>
              <DialogTitle>Report sick day</DialogTitle>
            </DialogHeader>
            <p id="portal-sick-desc" className="sr-only">Select the date you are sick</p>
            <form onSubmit={handleSickDay} className="space-y-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={sickDate}
                  onChange={(e) => setSickDate(e.target.value)}
                  required
                  data-testid="portal-sick-date"
                />
              </div>
              <Button type="submit" className="w-full" data-testid="portal-sick-submit">
                Submit
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
